import config from '@/config'

const backendUrl = config.backendUrl

// Cache TTL: 5 minutes — avoid hitting /api/users/me on every navigation
const CACHE_TTL_MS = 5 * 60 * 1000
let _lastVerified = 0

const authService = {
  /**
   * Checks if the current user is authenticated by hitting /api/users/me.
   * Returns the user object if authenticated, null otherwise.
   */
  async getCurrentUser() {
    try {
      const res = await fetch(`${backendUrl}/api/users/me`, {
        credentials: 'include',
      })
      if (res.ok) {
        const user = await res.json()
        localStorage.setItem('user', JSON.stringify(user))
        _lastVerified = Date.now()
        return user
      }
      // Backend returned non-200 — clear stale cache
      localStorage.removeItem('user')
      _lastVerified = 0
      return null
    } catch (e) {
      console.error('Erro ao verificar autenticação:', e)
      return null
    }
  },

  /**
   * Returns the cached user if it was verified recently (within CACHE_TTL_MS).
   * Returns null if the cache is stale or empty.
   */
  getVerifiedCachedUser() {
    if (Date.now() - _lastVerified > CACHE_TTL_MS) {
      return null
    }
    try {
      const data = localStorage.getItem('user')
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  },

  /**
   * Returns the cached user from localStorage (fast, synchronous).
   * Does NOT check freshness — use getVerifiedCachedUser() for guarded access.
   */
  getCachedUser() {
    try {
      const data = localStorage.getItem('user')
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  },

  /**
   * Redirects the browser to the backend Google OAuth login endpoint.
   */
  loginWithGoogle() {
    window.location.href = `${backendUrl}/api/auth/google/login`
  },

  /**
   * Local-only bypass — redirects to the backend's dev login endpoint,
   * which 404s outside ENVIRONMENT=development. See src/auth/auth.py.
   */
  loginDev() {
    window.location.href = `${backendUrl}/api/auth/dev/login`
  },

  /**
   * Calls the backend to revoke the token and clears local state.
   */
  async logout() {
    try {
      await fetch(`${backendUrl}/api/auth/revoke-token`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (e) {
      console.error('Erro ao revogar token:', e)
    }
    // Always clear local state regardless of backend response
    localStorage.removeItem('user')
    _lastVerified = 0
  },

  /**
   * Quick check: does the user have an access_token cookie?
   * This is a heuristic — the cookie is httpOnly so we can't read it directly.
   * We rely on a fast HEAD-like call to /api/auth/check.
   */
  async isAuthenticated() {
    try {
      const res = await fetch(`${backendUrl}/api/auth/check`, {
        credentials: 'include',
      })
      return res.ok
    } catch {
      return false
    }
  },
}

export default authService
